<script lang="ts">
	import { create, fromJson, toJson, type JsonObject } from "@bufbuild/protobuf";
	import { timestampDate } from "@bufbuild/protobuf/wkt";
	import { Heading, Input } from "flowbite-svelte-v1";
	import { getContext, onMount } from "svelte";
	import { Order, PaginationArgumentsSchema, type PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
	import { PollFilterSchema, PollSchema, type Poll } from "#gen/forms/v1/poll_pb.js";
	import type { User } from "#gen/identities/v1/identities_pb.js";
	import type { Component } from "#gen/pages/v2/page_pb.js";
	import { DEFAULT_PAGE_SIZE } from "#lib/api/api.js";
	import { AssetState } from "#lib/api/bundles.js";
	import { createNextPageArguments } from "#lib/api/pagination.js";
	import type { PollsClient } from "#lib/api/polls.js";
	import Badge from "#lib/components/Badge.svelte";
	import ContentEditable from "#lib/components/ContentEditable.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import InfiniteScroll from "#lib/components/InfiniteScroll.svelte";
	import Label from "#lib/components/Label.svelte";
	import Modal from "#lib/components/Modal.svelte";
	import P from "#lib/components/P.svelte";
	import type { Options, PopFn, PushFn } from "#lib/components/StackController.svelte";
	import { DEFAULT_POLL_QUESTIONS } from "#lib/components/flow/editor/appearance/constants.js";
	import type { ComponentEditingProps } from "#lib/components/page/ComponentProps.js";
	import Editor from "#lib/components/page/components/poll/Editor.svelte";
	import { DEFAULT_NEW_SERIES_NAME, DEFAULT_WINDOW_FORMAT_OPTIONS } from "#lib/components/page/components/poll/constants.js";
	import { resetEditorState } from "#lib/components/page/components/poll/utils.js";
	import { deepClone } from "#lib/deepClone.js";
	import { autofocus } from "#lib/directives/autofocus.js";
	import { Logger } from "#lib/logging.js";
	import { objectValue, setStructValue, structValue } from "#lib/struct.js";
	import { formatTimestamp } from "#lib/timestamp.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Writable } from "svelte/store";

	const pollsClient: PollsClient = getContext("pollsClient");
	const user: Writable<User> = getContext("user");
	const push: PushFn = getContext("push");
	const pop: PopFn = getContext("pop");

	const { id, page, data, onChange }: ComponentEditingProps = $props();

	const editorState: Writable<Record<string, unknown>> = getContext("editorState");
	const config = $derived(deepClone(structValue($page.config, id)) as JsonObject | undefined);
	const pollData = $derived(deepClone(structValue($data, id)) as JsonObject | undefined);
	const poll = $derived(fromJson(PollSchema, objectValue(pollData, "poll") ?? {}, { ignoreUnknownFields: true }));
	const filter = $derived(create(PollFilterSchema, { opaqueId: $page.id, states: [AssetState.ACTIVE] }));

	let additional: Poll[] = $state([]);
	let pageInfo: PaginationPageInfo | undefined = $state();
	let selection: { id: string; version: string } | undefined = $state();
	let showAddSeriesModal = $state(false);
	let newSeriesName: string | undefined = $state();
	let loading: boolean = $state(false);

	const all = $derived([poll, ...additional]);
	let selected = $derived(selection ? all.find(p => p.id === selection?.id && p.version === selection.version) : undefined);

	let controller: AbortController | undefined;

	onMount(() => {
		handleLoadMore();
		return () => {
			reset(poll);
		};
	});

	$effect(() => {
		// Handle new draft
		if (selection && poll.state === AssetState.DRAFT) {
			if (selection.id === poll.id && selection.version !== poll.version) {
				selection.version = poll.version;
				selected = poll;
			}
		}
	});

	$effect(() => {
		// Handle revert draft
		if (selection && !selected) {
			handlePop();
		}
	});

	function handleInput(poll: Poll) {
		update(poll);
	}

	function handleChange(poll: Poll | undefined) {
		if (!poll) {
			return;
		}
		update(poll);
		onChange?.(config);
	}

	function update(poll: Poll) {
		if (!$data) {
			$data = {} as JsonObject;
		}
		const value = { poll: toJson(PollSchema, poll) };
		setStructValue($data, id, value);
		$data = $data;
		$page = $page;
	}

	function reset(poll: Poll) {
		$editorState[id] = undefined;
		$editorState = resetEditorState($editorState, poll);
	}

	function handlePop() {
		if (selected) {
			reset(selected);
		}
		selection = undefined;
		pop();
	}

	function handlePollClick(e: MouseEvent, poll: Poll, readOnly = true) {
		e.stopImmediatePropagation();
		selection = { id: poll.id, version: poll.version };
		push(content, { backButtonSnippet: back, props: { readOnly } });
	}

	function handleShowAddPollSeries(e: MouseEvent) {
		e.stopImmediatePropagation();
		showAddSeriesModal = true;
	}

	function handleCancelAddPollSeries(e: MouseEvent) {
		e.stopImmediatePropagation();
		showAddSeriesModal = false;
	}

	function findComponent(components: Component[], id: string): Component | undefined {
		for (const c of components) {
			if (c.id === id) {
				return c;
			}
			const found = findComponent(c.children, id);
			if (found) {
				return found;
			}
		}
		return undefined;
	}

	async function handleAddPollSeries(e: MouseEvent) {
		e.stopImmediatePropagation();
		loading = true;
		try {
			const res = await pollsClient.createPoll($page.id, create(PollSchema, { orgId: poll.orgId, workspaceId: poll.workspaceId, title: newSeriesName || DEFAULT_NEW_SERIES_NAME, questions: DEFAULT_POLL_QUESTIONS }));
			const component = findComponent($page.components, id);
			if (component) {
				component.assetId = res.id;
				component.assetVersion = res.version;
			}
			handleChange(res);
			showAddSeriesModal = false;
		} catch (err) {
			Logger.error("failed to add poll series", { err });
		} finally {
			loading = false;
		}
	}

	async function handleLoadMore(scroll = false) {
		controller?.abort();
		controller = new AbortController();
		try {
			const pagination = pageInfo && scroll ? createNextPageArguments(pageInfo, "created_at", Order.DESC) : create(PaginationArgumentsSchema, { orderBy: "created_at", order: Order.DESC, first: DEFAULT_PAGE_SIZE });
			const res = await pollsClient.listPolls($page.orgId, $page.workspaceId, filter, pagination, { signal: controller.signal });
			if (scroll) {
				additional = [...additional, ...res.polls];
			} else {
				additional = res.polls;
			}
			pageInfo = res.pageInfo;
		} catch (err) {
			Logger.error("failed to load polls", { err });
		}
	}
</script>

{#snippet item(item: Poll, readOnly = true)}
	<button type="button" class="flex flex-col gap-2 rounded-xl border p-4 text-left hover:border-gray-400" onclick={e => handlePollClick(e, item, readOnly)}>
		<div class="flex w-full items-center justify-between gap-2">
			<Heading tag="h2" class="grow text-base font-medium">{item.title}</Heading>
			<div class="flex-none">
				{#if item.state === AssetState.DRAFT}
					<Badge color="red" border={true}>Draft</Badge>
				{:else if item.state === AssetState.ARCHIVED || (item.window?.end && timestampDate(item.window.end) < new Date())}
					<Badge color="gray" border={true}>Past</Badge>
				{:else if item.window?.start && timestampDate(item.window.start) > new Date()}
					<Badge color="green" border={true}>Upcoming</Badge>
				{:else}
					<Badge color="green" border={true}>Live</Badge>
				{/if}
			</div>
			<div class="flex-none">
				<Icon iconSet="heroicons" class="h-4 w-4" name="arrow-right" />
			</div>
		</div>
		<div class="flex w-full gap-4">
			{#if item.window}
				<P size="sm" secondary>
					{formatTimestamp(item.window.start, $user.timezone, undefined, DEFAULT_WINDOW_FORMAT_OPTIONS)}
					{#if item.window.end}
						- {formatTimestamp(item.window.end, $user.timezone, undefined, DEFAULT_WINDOW_FORMAT_OPTIONS)}
					{/if}
				</P>
			{/if}
			<P size="sm" secondary>
				{#if item.state === AssetState.DRAFT}
					Not published
				{:else if item.state === AssetState.ARCHIVED || (item.window?.end && timestampDate(item.window.end) < new Date())}
					Closed
					{#if item.window?.end && item.archivedAt && timestampDate(item.window.end) > timestampDate(item.archivedAt)}
						early
					{/if}
				{:else if item.window?.start && timestampDate(item.window.start) > new Date()}
					Not open
				{:else}
					Open
				{/if}
			</P>
		</div>
	</button>
{/snippet}

{#snippet back(_options: Options, _pop: PopFn)}
	{#if selected}
		<div class="flex w-full items-center gap-2 text-lg font-bold text-gray-900">
			<Button data-action-name="stack-controller-back" data-action-title="Back" color="none" class="flex gap-4 px-0 py-0" on:click={() => handlePop()}>
				<Icon name="arrow-left" class="h-6 w-6" />
				<Heading tag="h6" class="underline underline-offset-2">Poll</Heading>
			</Button>
			<div>/</div>
			<ContentEditable bind:value={selected.title} onCommit={() => handleChange(selected)} />
		</div>
	{/if}
{/snippet}

{#snippet content(options: Options)}
	{#if selected}
		<Editor {id} {page} poll={selected} readOnly={!!options.props?.readOnly} onInput={handleInput} onChange={handleChange} />
	{/if}
{/snippet}

{#if showAddSeriesModal}
	<Modal size="md" title="Are you sure you want to create a new series?" bind:open={showAddSeriesModal}>
		<div class="flex flex-col gap-4">
			<P size="sm" secondary>Adding a new series creates an editable draft to update the poll content. The Live series remains visible to visitors until the draft is published and the poll window is closed. Results for all series remain available in Analytics.</P>
			<div class="flex flex-col gap-1">
				<Label for="series-name" class="inline-block text-gray-900">Series name</Label>
				<div use:autofocus>
					<Input type="text" name="series-name" id="series-name" placeholder={DEFAULT_NEW_SERIES_NAME} maxlength={80} bind:value={newSeriesName} autocomplete="off" />
				</div>
			</div>
		</div>
		<svelte:fragment slot="footer">
			<div class="flex w-full justify-end gap-2">
				<Button data-action-name="create-series-cancel" color="alternative" on:click={handleCancelAddPollSeries}>Cancel</Button>
				<Button data-action-name="create-series-create" color="primary" {loading} on:click={handleAddPollSeries}>Add new series</Button>
			</div>
		</svelte:fragment>
	</Modal>
{/if}

<div class="flex flex-col gap-6 p-6">
	<div class="flex items-start gap-4">
		<div class="flex grow flex-col gap-2">
			<div class="flex items-center justify-between gap-2">
				<Heading tag="h4" class="flex items-center justify-between text-lg font-semibold">Series</Heading>
				<Button color="alternative" data-action-name="add-poll-series" class="flex flex-none gap-2" on:click={e => handleShowAddPollSeries(e)}>
					Add new poll series
					<Icon iconSet="heroicons" class="h-4 w-4" name="plus" />
				</Button>
			</div>
			<P size="sm" secondary>A series is one version of this poll. Start a new series when you need updated questions within the same conversion flow. Results for every series can be found in Analytics.</P>
		</div>
	</div>
	{@render item(poll, false)}
	{#each additional as current (current.id)}
		{#if current.id !== poll.id || current.version !== poll.version}
			{@render item(current, true)}
		{/if}
	{/each}
	<InfiniteScroll threshold={0.1} hasMore={pageInfo?.hasNextPage} onLoadMore={handleLoadMore} />
</div>
